// Shared shapes and helpers for the merchant Terms & Conditions pages.
// Strings in "titled", "para" and "bullets" blocks may wrap bold runs in **…**.

// a paragraph, or a bullet list, inside a titled clause
export type TncBody = string | { bullets: string[] };

export type TncBlock =
  | { kind: "clause"; label?: string; text: string }
  | { kind: "titled"; title: string; body: TncBody[] }
  | { kind: "note"; text: string; strong?: boolean }
  | { kind: "para"; text: string }
  | { kind: "bullets"; items: string[] }
  | { kind: "step"; title: string }
  | { kind: "dash"; label?: string; text: string }
  | { kind: "numbered"; items: string[] }
  | { kind: "callout"; title: string; paragraphs: string[] }
  | { kind: "table"; head?: [string, string]; rows: [string, string][] };

export type TncSection =
  | { kind: "clauses"; id: string; title: string; blocks: TncBlock[] }
  | { kind: "faq"; id: string; title: string; items: { q: string; a: string }[] }
  | {
      kind: "sla";
      id: string;
      title: string;
      head: [string, string];
      rows: [string, string][];
      note: string;
    }
  | { kind: "text"; id: string; title: string; text: string }
  | {
      kind: "contact";
      id: string;
      title: string;
      intro?: string;
      items: { label: string; value: string; href: string }[];
      after?: TncBlock[];
    };

export interface TncHero {
  breadcrumb: string;
  current: string;
  title: string;
  lines: string[];
}

// unnumbered opening block shown before section 01
export interface TncPreface {
  title: string;
  paragraphs: string[];
}

export const titled = (title: string, ...body: TncBody[]): TncBlock => ({
  kind: "titled",
  title,
  body,
});
export const para = (text: string): TncBlock => ({ kind: "para", text });
export const bullets = (...items: string[]): TncBlock => ({
  kind: "bullets",
  items,
});
export const numbered = (...items: string[]): TncBlock => ({
  kind: "numbered",
  items,
});
export const callout = (title: string, ...paragraphs: string[]): TncBlock => ({
  kind: "callout",
  title,
  paragraphs,
});

export const clause = (text: string, label?: string): TncBlock => ({
  kind: "clause",
  label,
  text,
});
export const note = (text: string, strong = false): TncBlock => ({
  kind: "note",
  text,
  strong,
});
export const step = (title: string): TncBlock => ({ kind: "step", title });
export const dash = (text: string, label?: string): TncBlock => ({
  kind: "dash",
  label,
  text,
});

// identical on every IZI plan
export const slaSection: TncSection = {
  kind: "sla",
  id: "service-commitment",
  title: "Service commitment",
  head: ["Activity", "Indicative SLA"],
  rows: [
    ["Claim acknowledgement", "Within 24 working hours"],
    ["Initial assessment", "24–48 working hours"],
    ["Standard repair cases", "Typically 5–7 working days"],
    ["Replacement cases", "Subject to product availability"],
    ["Complex investigations", "Subject to operational review"],
  ],
  note: "All timelines are in working days and working hours, and are subject to clause 8.",
};

export const contactSection: TncSection = {
  kind: "contact",
  id: "contact-us",
  title: "Contact us",
  items: [
    {
      label: "Email",
      value: "support@protega.ai",
      href: "mailto:support@protega.ai",
    },
    {
      label: "Web",
      value: "www.protega.ai/customer-login",
      href: "https://www.protega.ai/customer-login",
    },
    {
      label: "WhatsApp",
      value: "+91 93240 95283",
      href: "https://wa.me/919324095283",
    },
  ],
};
